/**
 * Paid Social report (task 035-10) — "Paid Social" mirrors CTO Email with
 * `utm_source` as the source and the campaign as the link (README decision
 * 9): one row per traffic source, a click-in to the source's campaigns with
 * the same counts, cost where priced, per-campaign `All` / `Unique` / `Rejected`
 * exports scoped by the row's `utmSource`.
 *
 * Level 1 = `data.sources[]`: the traffic-source cell is a button that drills
 * into level 2 = `data.campaigns[]` filtered to that source. Level 2's totals
 * row is the SELECTED SOURCE's own row from `sources[]` — never the sum of
 * the campaign rows (task 03's rule — an address under two campaigns of one
 * source is one distinct address). A row whose `utmSource === ''` renders as
 * *(no utm_source)* — the DTO carries the raw source (task 09's contract);
 * the label is this screen's.
 *
 * `CAMPAIGN_LEADS_EXPORT_AVAILABLE` (task 07) gates every export button on
 * this screen — `false` until task 07 lands (`paid-social-stats-api.ts`).
 *
 * Talks to the api through the screen-local `paid-social-stats-api.ts`
 * (A2 (b)) and the shared `api` client for the campaign dropdown only (007's
 * precedent).
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type CampaignDetailResponse } from '../../lib/api';
import { pageUrl } from '../../lib/campaign-styles';
import { fmtDate, fmtMoney } from '../../lib/format';
import { Card } from '../../components/ui/card';
import { Select } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  CAMPAIGN_LEADS_EXPORT_AVAILABLE,
  fetchPaidSocialStats,
  paidSocialLeadsExportUrl,
  type PaidSocialCampaignRow,
  type PaidSocialLeadsVariant,
  type PaidSocialSourceRow,
  type PaidSocialStatsResponse,
} from './paid-social-stats-api';
import { ReportShell } from './report-shell';
import {
  RangeFilters,
  ReportSearch,
  SearchNote,
  TestLeadsToggle,
  matchesSearch,
  useReportRange,
  useSharedReportFilters,
} from './report-filters';
import {
  cell,
  EmptyRow,
  ErrorBanner,
  LoadingRow,
  SortableHeader,
  UNIQUE_TOTAL_TITLE,
  useClientSort,
} from './report-table';

/** README decision 9 / task 10 §Scope 2 — the no-source label, never the DTO value. */
const NO_SOURCE_LABEL = '(no utm_source)';
const NO_SOURCE_TITLE =
  'These leads carried no utm_source — the raw acquisition value is empty; exporting this row scopes to the no-source slice of the campaign';

/** Level-1 totals row's `Cost` title — task 10 §Scope 2, verbatim. */
const TOTALS_COST_TITLE =
  'Adds up the traffic-source rows — an address that arrived under two sources is costed under each';

/**
 * Level-2 (campaign grain) `Cost` title, on both the column header and the
 * totals row: the contrast on screen — the totals row is the selected
 * source's own cost, not the sum of the campaign rows below it. The task
 * file does not quote a literal for this one (only the level-1 sentence is
 * quoted); wording chosen to state the same contrast task 03's
 * `LEVEL2_COST_TITLE` states for CTO — logged under Decisions made.
 */
const LEVEL2_COST_TITLE = "This source's cost across its campaigns — not the sum of the rows below";

/** `NN.NN %`, empty when `null` (uniqueLeads = 0 — the contract's rule). */
function fmtNewToFile(pct: number | null): string {
  if (pct === null) return '';
  return `${pct.toFixed(2)} %`;
}

interface SourceLabel {
  text: string;
  title?: string;
}

/** `''` (no source) renders as this screen's `(no utm_source)` label — never the DTO value. */
function sourceLabel(utmSource: string): SourceLabel {
  return utmSource === '' ? { text: NO_SOURCE_LABEL, title: NO_SOURCE_TITLE } : { text: utmSource };
}

type SourceSortKey =
  | 'utmSource'
  | 'leads'
  | 'uniqueLeads'
  | 'success'
  | 'duplicate'
  | 'unchecked'
  | 'rejected'
  | 'newToFilePct'
  | 'cost';

function sourceAccessor(row: PaidSocialSourceRow, key: SourceSortKey): string | number | null {
  switch (key) {
    case 'utmSource':
      return row.utmSource;
    case 'leads':
      return row.leads;
    case 'uniqueLeads':
      return row.uniqueLeads;
    case 'success':
      return row.success;
    case 'duplicate':
      return row.duplicate;
    case 'unchecked':
      return row.unchecked;
    case 'rejected':
      return row.rejected;
    case 'newToFilePct':
      return row.newToFilePct;
    case 'cost':
      return row.cost;
  }
}

type CampaignSortKey =
  | 'pageTitle'
  | 'startDate'
  | 'endDate'
  | 'pricePerLead'
  | 'leads'
  | 'uniqueLeads'
  | 'success'
  | 'duplicate'
  | 'unchecked'
  | 'rejected'
  | 'newToFilePct'
  | 'cost';

function campaignAccessor(
  row: PaidSocialCampaignRow,
  key: CampaignSortKey
): string | number | null {
  switch (key) {
    case 'pageTitle':
      return row.pageTitle ?? row.campaignName;
    case 'startDate':
      return row.startDate;
    case 'endDate':
      return row.endDate;
    case 'pricePerLead':
      return row.pricePerLead;
    case 'leads':
      return row.leads;
    case 'uniqueLeads':
      return row.uniqueLeads;
    case 'success':
      return row.success;
    case 'duplicate':
      return row.duplicate;
    case 'unchecked':
      return row.unchecked;
    case 'rejected':
      return row.rejected;
    case 'newToFilePct':
      return row.newToFilePct;
    case 'cost':
      return row.cost;
  }
}

const EXPORT_LABELS: Record<PaidSocialLeadsVariant, string> = {
  all: 'All',
  unique: 'Unique',
  rejected: 'Rejected',
};

/** The per-campaign-row export buttons — `window.open`s `campaign-leads.csv` scoped to this row's `utmSource`. */
function ExportButtons({
  campaignId,
  onExport,
}: {
  campaignId: string;
  onExport: (variant: PaidSocialLeadsVariant) => void;
}) {
  if (!CAMPAIGN_LEADS_EXPORT_AVAILABLE) return null;
  const variants: PaidSocialLeadsVariant[] = ['all', 'unique', 'rejected'];
  return (
    <div className="flex justify-end gap-1">
      {variants.map((variant) => (
        <button
          key={variant}
          type="button"
          data-testid={`paid-social-export-${variant}-${campaignId}`}
          onClick={() => onExport(variant)}
          className="rounded-md px-1.5 py-0.5 text-xs font-medium text-accent hover:underline"
        >
          {EXPORT_LABELS[variant]}
        </button>
      ))}
    </div>
  );
}

/** A source matches by its displayed label (`(no utm_source)` for an empty one). */
function matchesSource(q: string, source: PaidSocialSourceRow): boolean {
  return matchesSearch(q, sourceLabel(source.utmSource).text);
}

/** A campaign matches by its name, page title or page slug. */
function matchesCampaign(q: string, campaign: PaidSocialCampaignRow): boolean {
  return matchesSearch(q, campaign.campaignName, campaign.pageTitle, campaign.pageSlug);
}

const LEVEL1_COLSPAN = 9;
const LEVEL2_COLSPAN = 13;

export function PaidSocialStats() {
  const navigate = useNavigate();
  const range = useReportRange();
  const { from, to } = range;
  const [campaignId, setCampaignId] = useState('');
  // Test leads are shared across the style tabs (URL-backed, 087-01); Paid Social has no status filter.
  const { includeTest, setIncludeTest } = useSharedReportFilters();
  const [search, setSearch] = useState('');
  const [campaigns, setCampaigns] = useState<CampaignDetailResponse[]>([]);
  const [data, setData] = useState<PaidSocialStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUtmSource, setSelectedUtmSource] = useState<string | null>(null);

  // Dropdown loader — the shared typed client (best-effort: an empty dropdown
  // list degrades the filter, not the report).
  useEffect(() => {
    api
      .getCampaigns()
      .then(setCampaigns)
      .catch(() => {
        // Leave the previous (empty) list on error.
      });
  }, []);

  // Fetch on every filter change; the stale response of a superseded filter
  // set is dropped rather than rendered (co-op-counts.tsx's idiom).
  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPaidSocialStats({ from, to, includeTest, campaignId })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Request failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, includeTest, campaignId]);

  // A filter change can drop the drilled-into source from the response (or
  // fail the fetch entirely) — fall back to level 1 rather than show a level
  // 2 for a source no longer in range.
  useEffect(() => {
    if (
      selectedUtmSource !== null &&
      data &&
      !data.sources.some((s) => s.utmSource === selectedUtmSource)
    ) {
      setSelectedUtmSource(null);
    }
  }, [data, selectedUtmSource]);

  const paidSocialCampaignOptions = useMemo(
    () => campaigns.filter((c) => c.campaignStyle === 'paid_social'),
    [campaigns]
  );

  // The search narrows the rows only — the footers keep the server's totals (087-01 D5).
  const searchActive = search.trim() !== '';

  // Level 1: a source shows when its displayed label matches or any of its campaigns does.
  const searchedSources = useMemo(
    () =>
      (data?.sources ?? []).filter(
        (source) =>
          matchesSource(search, source) ||
          (data?.campaigns ?? []).some(
            (c) => c.utmSource === source.utmSource && matchesCampaign(search, c)
          )
      ),
    [data, search]
  );

  const {
    sortedRows: sortedSources,
    sort: sourceSort,
    toggleSort: toggleSourceSort,
  } = useClientSort<PaidSocialSourceRow, SourceSortKey>(searchedSources, sourceAccessor);

  const selectedSource = useMemo(
    () =>
      selectedUtmSource !== null
        ? (data?.sources.find((s) => s.utmSource === selectedUtmSource) ?? null)
        : null,
    [data, selectedUtmSource]
  );

  const allCampaignsForSource = useMemo(
    () =>
      selectedUtmSource !== null
        ? (data?.campaigns.filter((c) => c.utmSource === selectedUtmSource) ?? [])
        : [],
    [data, selectedUtmSource]
  );

  // Level 2: a source that matched itself shows all its campaigns, else only the matching ones.
  const campaignsForSource = useMemo(
    () =>
      selectedSource === null || matchesSource(search, selectedSource)
        ? allCampaignsForSource
        : allCampaignsForSource.filter((c) => matchesCampaign(search, c)),
    [allCampaignsForSource, selectedSource, search]
  );

  const {
    sortedRows: sortedCampaigns,
    sort: campaignSort,
    toggleSort: toggleCampaignSort,
  } = useClientSort<PaidSocialCampaignRow, CampaignSortKey>(campaignsForSource, campaignAccessor);

  const exportCampaign = (row: PaidSocialCampaignRow) => (variant: PaidSocialLeadsVariant) => {
    window.open(
      paidSocialLeadsExportUrl({
        campaignId: row.campaignId,
        utmSource: row.utmSource,
        from,
        to,
        includeTest,
        variant,
      })
    );
  };

  return (
    <ReportShell
      title="Paid Social"
      desc="Leads by traffic source (utm_source), then by landing page. Cost = CPL × unique where a price is set. Test leads hidden by default."
    >
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
          <RangeFilters
            preset={range.preset}
            from={range.from}
            to={range.to}
            applyPreset={range.applyPreset}
            setRange={range.setRange}
            testIdPrefix="paid-social"
          />

          <Select
            aria-label="Campaign"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            className="h-9 min-w-[180px]"
          >
            <option value="">All campaigns</option>
            {paidSocialCampaignOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>

          <TestLeadsToggle checked={includeTest} onChange={setIncludeTest} />

          <ReportSearch
            value={search}
            onChange={setSearch}
            placeholder="Search source, campaign or page"
            testId="paid-social-search"
          />

          {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>

        <SearchNote
          active={searchActive}
          shown={selectedSource ? sortedCampaigns.length : sortedSources.length}
          total={selectedSource ? allCampaignsForSource.length : (data?.sources.length ?? 0)}
          testId="paid-social-search-note"
        />

        {error && <ErrorBanner message={error} />}

        {selectedUtmSource !== null && selectedSource && (
          <div className="flex items-center gap-3 border-b border-line px-4 py-3">
            <button
              type="button"
              data-testid="paid-social-back"
              onClick={() => setSelectedUtmSource(null)}
              className="text-sm font-medium text-accent hover:underline"
            >
              ← All sources
            </button>
            <h2
              className="text-sm font-semibold text-ink"
              title={sourceLabel(selectedSource.utmSource).title}
            >
              {sourceLabel(selectedSource.utmSource).text}
            </h2>
          </div>
        )}

        <div className="overflow-x-auto">
          {selectedUtmSource === null || !selectedSource ? (
            <Table data-testid="paid-social-table">
              <TableHeader>
                <TableRow>
                  <SortableHeader
                    label="Traffic source"
                    sortKey="utmSource"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Total leads"
                    sortKey="leads"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Unique"
                    sortKey="uniqueLeads"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Success"
                    sortKey="success"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Duplicate"
                    sortKey="duplicate"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Not checked"
                    sortKey="unchecked"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Rejected"
                    sortKey="rejected"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="New to File"
                    sortKey="newToFilePct"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Cost"
                    sortKey="cost"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data === null ? (
                  <LoadingRow colSpan={LEVEL1_COLSPAN} />
                ) : sortedSources.length === 0 ? (
                  <EmptyRow
                    colSpan={LEVEL1_COLSPAN}
                    message={
                      data.sources.length > 0 && searchActive
                        ? `No rows match "${search.trim()}".`
                        : 'No traffic sources in this range.'
                    }
                  />
                ) : (
                  sortedSources.map((source) => {
                    const label = sourceLabel(source.utmSource);
                    return (
                      <TableRow
                        key={source.utmSource}
                        data-testid={`paid-social-row-${source.utmSource || 'none'}`}
                      >
                        <TableCell title={label.title}>
                          <button
                            type="button"
                            onClick={() => setSelectedUtmSource(source.utmSource)}
                            className="text-accent hover:underline"
                          >
                            {label.text}
                          </button>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(source.leads)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(source.uniqueLeads)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(source.success)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(source.duplicate)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(source.unchecked)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(source.rejected)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtNewToFile(source.newToFilePct)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtMoney(source.cost)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
              {data !== null && data.sources.length > 0 && (
                <TableFooter>
                  <TableRow data-testid="paid-social-totals-row">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {cell(data.totals.leads)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                      {cell(data.totals.uniqueLeads)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                      {cell(data.totals.success)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                      {cell(data.totals.duplicate)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                      {cell(data.totals.unchecked)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {cell(data.totals.rejected)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtNewToFile(data.totals.newToFilePct)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={TOTALS_COST_TITLE}>
                      {fmtMoney(data.totals.cost)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          ) : (
            <Table data-testid="paid-social-table">
              <TableHeader>
                <TableRow>
                  <SortableHeader
                    label="Page title"
                    sortKey="pageTitle"
                    sort={campaignSort}
                    onToggle={toggleCampaignSort}
                  />
                  <SortableHeader
                    label="Start"
                    sortKey="startDate"
                    sort={campaignSort}
                    onToggle={toggleCampaignSort}
                  />
                  <SortableHeader
                    label="End"
                    sortKey="endDate"
                    sort={campaignSort}
                    onToggle={toggleCampaignSort}
                  />
                  <SortableHeader
                    label="CPL"
                    sortKey="pricePerLead"
                    sort={campaignSort}
                    onToggle={toggleCampaignSort}
                  />
                  <SortableHeader
                    label="Total leads"
                    sortKey="leads"
                    sort={campaignSort}
                    onToggle={toggleCampaignSort}
                  />
                  <SortableHeader
                    label="Unique"
                    sortKey="uniqueLeads"
                    sort={campaignSort}
                    onToggle={toggleCampaignSort}
                    title={UNIQUE_TOTAL_TITLE}
                  />
                  <SortableHeader
                    label="Success"
                    sortKey="success"
                    sort={campaignSort}
                    onToggle={toggleCampaignSort}
                    title={UNIQUE_TOTAL_TITLE}
                  />
                  <SortableHeader
                    label="Duplicate"
                    sortKey="duplicate"
                    sort={campaignSort}
                    onToggle={toggleCampaignSort}
                    title={UNIQUE_TOTAL_TITLE}
                  />
                  <SortableHeader
                    label="Not checked"
                    sortKey="unchecked"
                    sort={campaignSort}
                    onToggle={toggleCampaignSort}
                    title={UNIQUE_TOTAL_TITLE}
                  />
                  <SortableHeader
                    label="Rejected"
                    sortKey="rejected"
                    sort={campaignSort}
                    onToggle={toggleCampaignSort}
                  />
                  <SortableHeader
                    label="New to File"
                    sortKey="newToFilePct"
                    sort={campaignSort}
                    onToggle={toggleCampaignSort}
                  />
                  <SortableHeader
                    label="Cost"
                    sortKey="cost"
                    sort={campaignSort}
                    onToggle={toggleCampaignSort}
                    title={LEVEL2_COST_TITLE}
                  />
                  <TableHead className="text-right">Exports</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedCampaigns.length === 0 ? (
                  <EmptyRow
                    colSpan={LEVEL2_COLSPAN}
                    message={
                      allCampaignsForSource.length > 0 && searchActive
                        ? `No rows match "${search.trim()}".`
                        : 'No leads in this range.'
                    }
                  />
                ) : (
                  sortedCampaigns.map((row) => {
                    const live = pageUrl(row.pageSlug);
                    return (
                      <TableRow
                        key={row.campaignId}
                        data-testid={`paid-social-campaign-row-${row.utmSource || 'none'}-${row.campaignId}`}
                      >
                        <TableCell>
                          <button
                            type="button"
                            onClick={() => navigate(`/campaigns/${row.campaignId}`)}
                            className="text-accent hover:underline"
                          >
                            {row.pageTitle ?? row.campaignName}
                          </button>
                          {live && (
                            <a
                              href={live}
                              target="_blank"
                              rel="noreferrer"
                              aria-label="View live page"
                              className="ml-1 text-faint hover:text-ink"
                            >
                              ↗
                            </a>
                          )}
                        </TableCell>
                        <TableCell>{fmtDate(row.startDate)}</TableCell>
                        <TableCell>{fmtDate(row.endDate)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtMoney(row.pricePerLead)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{cell(row.leads)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(row.uniqueLeads)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(row.success)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(row.duplicate)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(row.unchecked)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(row.rejected)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtNewToFile(row.newToFilePct)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtMoney(row.cost)}
                        </TableCell>
                        <TableCell className="text-right">
                          <ExportButtons
                            campaignId={row.campaignId}
                            onExport={exportCampaign(row)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
              {allCampaignsForSource.length > 0 && (
                <TableFooter>
                  <TableRow data-testid="paid-social-totals-row">
                    <TableCell>Total</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell className="text-right tabular-nums">
                      {cell(selectedSource.leads)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                      {cell(selectedSource.uniqueLeads)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                      {cell(selectedSource.success)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                      {cell(selectedSource.duplicate)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                      {cell(selectedSource.unchecked)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {cell(selectedSource.rejected)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtNewToFile(selectedSource.newToFilePct)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={LEVEL2_COST_TITLE}>
                      {fmtMoney(selectedSource.cost)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          )}
        </div>
      </Card>
    </ReportShell>
  );
}
